import { Link } from "react-router-dom";
import { Users, Target, ShieldCheck, Download, ArrowRight } from "lucide-react";
import { Logo } from "../components/AppShell";
import { LegalFooter } from "../components/LegalFooter";

const VIDEO = "/promo/socialbetting-promo.mp4";
const COVER = "/promo/socialbetting-cover.jpg";
const STEPS = ["Crea la tua lega e invita gli amici con un codice.", "Compila la schedina prima del calcio d'inizio.", "Scala la classifica giornata dopo giornata."];
const WHY = [
  [Users, "Sfida tra amici", "Lega privata, classifica e sfottò."],
  [Target, "Mettiti alla prova", "Fisse, doppie e risultato esatto."],
  [ShieldCheck, "Divertimento senza rischi", "Solo punti virtuali, gratis."],
];
const RULES = [
  ["Fissa", "1, X o 2: l'esito secco della partita.", "text-emerald-400 border-emerald-500/40"],
  ["Doppia", "1X, X2 o 12: due esiti, meno rischio.", "text-blue-400 border-blue-500/40"],
  ["Risultato esatto", "Indovina il punteggio: punti extra.", "text-violet-400 border-violet-500/40"],
  ["Bonus giornata", "Tutti i pronostici giusti? Bonus.", "text-amber-400 border-amber-500/40"],
];
const COMPS = ["Serie A", "Champions League", "Premier League", "LaLiga", "Bundesliga", "Ligue 1", "Mondiali", "Europei"];

const Cta = ({ testId, children = "Inizia gratis" }) => (
  <Link to="/login?mode=register" data-testid={testId} className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold uppercase tracking-wider transition-colors">
    {children} <ArrowRight size={18} />
  </Link>
);

const Title = ({ kicker, children }) => (
  <div className="mb-8">
    <p className="font-mono text-xs uppercase tracking-widest text-emerald-400">{kicker}</p>
    <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2">{children}</h2>
  </div>
);

export default function LandingPage() {
  return (
    <div className="min-h-screen pitch-bg">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0A0D14]/80 border-b border-[#26334D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/login" data-testid="landing-login-button" className="text-sm font-semibold text-slate-300 hover:text-emerald-400">Accedi</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400">Il fantacalcio dei pronostici · gratis</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tight mt-4" data-testid="landing-headline">
            Sfida i tuoi amici.<br />Pronostica da esperto.<br /><span className="text-emerald-400">Zero soldi veri.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-xl">Leghe private, schedine su fisse, doppie e risultato esatto, classifica aggiornata ogni giornata. Solo punti virtuali e tanta gloria.</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Cta testId="landing-hero-cta" />
            <Link to="/login" data-testid="landing-hero-login" className="h-12 px-8 inline-flex items-center rounded-full border border-[#26334D] text-slate-200 hover:border-emerald-500/60 transition-colors">Accedi</Link>
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-col items-center gap-4">
          <video src={VIDEO} poster={COVER} autoPlay muted loop playsInline controls data-testid="landing-promo-video"
            className="w-full max-w-[320px] aspect-[9/16] rounded-2xl border border-[#26334D] bg-black object-cover shadow-2xl shadow-emerald-500/10" />
          <div className="flex gap-4 text-xs font-mono uppercase tracking-widest">
            <a href={VIDEO} download data-testid="download-video-link" className="flex items-center gap-1.5 text-emerald-400 hover:underline"><Download size={14} /> Video MP4</a>
            <a href={COVER} download data-testid="download-cover-link" className="flex items-center gap-1.5 text-slate-400 hover:text-white"><Download size={14} /> Copertina</a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <Title kicker="Come funziona">In 3 passi</Title>
        <ol className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <li key={s} className="card-surface p-6 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`landing-step-${i + 1}`}>
              <span className="font-display text-5xl font-black text-emerald-400">{i + 1}</span>
              <p className="mt-3 text-slate-200">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <Title kicker="Perché giocare">Tre buoni motivi</Title>
        <div className="grid md:grid-cols-3 gap-4">
          {WHY.map(([Icon, t, d]) => (
            <div key={t} className="card-surface p-6" data-testid={`landing-why-${t.split(" ")[0].toLowerCase()}`}>
              <Icon className="text-emerald-400" size={28} />
              <h3 className="font-display text-2xl font-bold uppercase mt-4">{t}</h3>
              <p className="text-slate-400 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <Title kicker="Le regole in breve">Come si fanno punti</Title>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {RULES.map(([t, d, cls]) => (
            <div key={t} className={`rounded-xl border bg-[#121824] p-6 ${cls}`}>
              <h3 className="font-display text-2xl font-black uppercase">{t}</h3>
              <p className="text-slate-300 text-sm mt-2">{d}</p>
            </div>
          ))}
        </div>
        <Link to="/regolamento" className="inline-block mt-6 text-xs font-mono uppercase tracking-widest text-emerald-400 hover:underline" data-testid="landing-rules-link">Leggi il regolamento completo</Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <Title kicker="Competizioni">Gioca sui campionati top</Title>
        <div className="flex flex-wrap gap-3">
          {COMPS.map((c) => <span key={c} className="px-5 py-2.5 rounded-full border border-[#26334D] bg-[#182030] font-semibold text-slate-200">{c}</span>)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="card-surface border-emerald-500/40 p-10 sm:p-14 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Crea la tua lega in 30 secondi</h2>
          <p className="text-slate-400 mt-3">Gratis, senza soldi veri, solo per la gloria.</p>
          <div className="mt-8"><Cta testId="landing-final-cta" /></div>
        </div>
      </section>
      <LegalFooter />
    </div>
  );
}
